/* eslint-disable react-refresh/only-export-components */
import type { QueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import type { CampaignDetailsApiClient } from '@/scopes/search/search/domain/campaign-details/types'
import * as React from 'react'
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams'
import { createSelectDateFilterActions, SelectDateFilterActions } from '@/scopes/search/search/domain/actions/selectDateFilterActions'
import { createSelectDestinationActions } from '@/scopes/search/search/domain/actions/selectDestinationActions'
import { createFilterSelectionQueryParamHandler } from '@/scopes/search/search/domain/filterSelectionQueryParamHandler'
import { searchAPIClient } from '@/scopes/search/search/infrastructure/api'
import { createCampaignDetailsApiClient } from '@/scopes/search/search/infrastructure/campaign-details/campaignDetailsApiClient'
import { createSearchQueryRepository } from '@/scopes/search/search/infrastructure/repositories/searchQueryRepository'
import { searchStore } from '@/scopes/search/search/infrastructure/store/store'
import { searchPassThroughParams, type SearchPassThroughParams } from '@/scopes/search/types'

const queryStringClientEmptyImplementation = {
  set: () => {
    throw new Error('Not implemented')
  },
  get: () => {
    throw new Error('Not implemented')
  },
  getValue: () => {
    throw new Error('Not implemented')
  },
  remove: () => {
    throw new Error('Not implemented')
  },
  replaceValue: () => {
    throw new Error('Not implemented')
  },
} as QueryStringClient

const defaultBrowserQuery = createBrowserQueryStringKeepingParams<SearchPassThroughParams>({
  passThroughParams: searchPassThroughParams,
  queryStringClient: queryStringClientEmptyImplementation,
})

const defaultFilterSelectionQueryParamHandler = createFilterSelectionQueryParamHandler({
  store: searchStore,
  browserQuery: defaultBrowserQuery,
})
const defaultSearchQueryRepository = createSearchQueryRepository()

const defaultDateFilterActions = createSelectDateFilterActions({
  filterSelectionQueryParamHandler: defaultFilterSelectionQueryParamHandler,
  searchQueryRepository: defaultSearchQueryRepository,
})
const defaultDestinationFilterActions = createSelectDestinationActions({
  filterSelectionQueryParamHandler: defaultFilterSelectionQueryParamHandler,
  searchQueryRepository: defaultSearchQueryRepository,
})

const SearchDependenciesContext = React.createContext<SearchDependencies>({
  searchAPIClient,
  campaignDetailsApiClient: createCampaignDetailsApiClient({ contextItemId: '' }),
  filterSelectionQueryParamHandler: defaultFilterSelectionQueryParamHandler,
  dateFilterActions: defaultDateFilterActions,
  destinationFilterActions: defaultDestinationFilterActions,
})

interface SearchDependencies {
  searchAPIClient: typeof searchAPIClient
  campaignDetailsApiClient: CampaignDetailsApiClient
  filterSelectionQueryParamHandler: ReturnType<typeof createFilterSelectionQueryParamHandler>
  dateFilterActions: SelectDateFilterActions
  destinationFilterActions: ReturnType<typeof createSelectDestinationActions>
}

export function SearchDependenciesProvider({
  children,
  dependencies,
}: React.PropsWithChildren<{ dependencies: SearchDependencies }>) {
  return <SearchDependenciesContext value={dependencies}>{children}</SearchDependenciesContext>
}

export const useSearchDependencies = () => React.use(SearchDependenciesContext)
